import { ChatbotDesignDTO, ThemeConfig } from "../types/theme.type";
import { DEFAULT_DESIGN, DEFAULT_CONFIG } from "../constants/const";
import { getHoverColor, getActiveColor } from "./color.utils";

// グローバル状態
let currentDesign: ChatbotDesignDTO = DEFAULT_DESIGN;
let currentConfig: ThemeConfig = DEFAULT_CONFIG;

/**
 * デザインをCSS変数に変換する
 * @param design デザイン
 * @param config テーマ設定
 * @returns CSS変数
 */
function designToCSSVariables(
  design: ChatbotDesignDTO,
  config: ThemeConfig = DEFAULT_CONFIG
): Record<string, string> {
  return {
    // レイアウト
    "--chat-alignment": design.alignment,
    "--chat-banner-margin-h": `${design.banner_margin_horizontal}px`,
    "--chat-banner-margin-v": `${design.banner_margin_vertical}px`,
    "--chat-margin-h": `${design.chat_margin_horizontal}px`,
    "--chat-margin-v": `${design.chat_margin_vertical}px`,
    "--chat-width": `min(${design.chat_display_width}px, calc(100vw - ${design.chat_margin_horizontal * 2}px))`,
    "--chat-height": `min(${design.chat_display_height}px, calc(100vh - ${design.chat_margin_vertical * 2}px))`,

    // ヘッダー
    "--header-bg": design.header_background_color,
    "--header-bg-hover": getHoverColor(design.header_background_color),
    "--header-bg-active": getActiveColor(design.header_background_color),
    "--header-text": design.header_text_color,
    "--header-font-size": `${design.header_font_size}px`,

    // 日付
    "--date-bg": design.date_background_color,
    "--date-text": design.date_text_color,

    // メイン
    "--main-bg": design.main_background_color,
    "--main-text": design.main_text_color,

    // ロボットメッセージ
    "--bot-bubble-border": design.bot_bubble_border_color,
    "--bot-bubble-bg": design.bot_bubble_background_color,
    "--bot-bubble-text": design.bot_bubble_text_color,
    "--bot-bubble-font-size": `${design.bot_bubble_font_size}px`,
    "--bot-bubble-link": design.bot_bubble_link_color,
    "--bot-bubble-link-hover": getHoverColor(design.bot_bubble_link_color),

    // ユーザーメッセージ
    "--user-bubble-border": design.user_bubble_border_color,
    "--user-bubble-bg": design.user_bubble_background_color,
    "--user-bubble-text": design.user_bubble_text_color,
    "--user-bubble-font-size": `${design.user_bubble_font_size}px`,

    // 解決ボタン
    "--resolved-btn-border": design.resolved_button_border_color,
    "--resolved-btn-bg": design.resolved_button_background_color,
    "--resolved-btn-bg-hover": getHoverColor(
      design.resolved_button_background_color,
      design.resolved_button_hover_background_color
    ),
    "--resolved-btn-bg-active": design.resolved_button_hover_background_color,
    "--resolved-btn-text": design.resolved_button_text_color,
    "--resolved-btn-label": design.resolved_button_label,

    // 未解決ボタン
    "--unresolved-btn-border": design.unresolved_button_border_color,
    "--unresolved-btn-bg": design.unresolved_button_background_color,
    "--unresolved-btn-bg-hover": getHoverColor(
      design.unresolved_button_background_color,
      design.unresolved_button_hover_background_color
    ),
    "--unresolved-btn-bg-active":
      design.unresolved_button_hover_background_color,
    "--unresolved-btn-text": design.unresolved_button_text_color,
    "--unresolved-btn-label": design.unresolved_button_label,

    // フロー選択肢ボタン
    "--flow-choice-border": design.flow_choice_border_color,
    "--flow-choice-bg": design.flow_choice_background_color,
    "--flow-choice-text": design.flow_choice_text_color,
    "--flow-choice-bg-hover": design.flow_choice_hover_background_color,

    // フッター
    "--footer-bg": design.footer_background_color,

    // 入力ボックス
    "--input-border": design.input_field_border_color,
    "--input-border-hover": getHoverColor(design.input_field_border_color),
    "--input-border-focus": design.input_field_border_color,
    "--input-bg": design.input_field_background_color,
    "--input-text": design.input_field_text_color,
    "--input-font-size": `${design.input_field_font_size}px`,
    "--input-placeholder": design.input_field_placeholder,
    "--input-placeholder-color": design.input_field_placeholder_text_color,

    // 送信ボタン
    "--send-btn-disabled": design.send_button_disabled_color,
    "--send-btn-enabled": design.send_button_enabled_color,
    "--send-btn-hover": getHoverColor(design.send_button_enabled_color),
    "--send-btn-active": getActiveColor(design.send_button_enabled_color),

    // ボットアイコン
    "--img-bot-avatar": `url(${config.botAvatar})`,
    // ユーザーアイコン、まだいないけど
    "--img-user-avatar": `url(${config.userAvatar})`,
    // ウィジェットボタンアイコン
    "--img-btn-init": `url(${config.buttonInitIcon})`,
    // 送信ボタンアイコン
    "--img-btn-send": `url(${config.buttonSendIcon})`,
  };
}

/**
 * CSS変数をDOMに注入する
 * @param design デザイン
 * @param config テーマ設定
 * @returns DOM
 */
function injectCSSToDOM(design: ChatbotDesignDTO, config: ThemeConfig): void {
  const rootElement =
    document.getElementById("chatbot-root") || document.documentElement;
  const cssVars = designToCSSVariables(design, config);

  Object.entries(cssVars).forEach(([key, value]) => {
    rootElement.style.setProperty(key, value as string);
  });
}

/**
 * テーマを初期化する
 * @param design デザイン
 * @param config テーマ設定
 * @returns テーマを初期化
 */
export function initializeTheme(
  design?: Partial<ChatbotDesignDTO>,
  config?: ThemeConfig
): void {
  currentDesign = { ...DEFAULT_DESIGN, ...design };
  currentConfig = { ...DEFAULT_CONFIG, ...config };
  injectCSSToDOM(currentDesign, currentConfig);
}

/**
 * テーマ設定を取得
 * @returns テーマ設定
 */
export function getThemeConfig(): ThemeConfig {
  return currentConfig;
}

/**
 * デザイン設定を取得
 * @returns デザイン設定
 */
export function getThemeDesign(): ChatbotDesignDTO {
  return currentDesign;
}
